import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Save, AlertTriangle } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminTextarea, AdminSelect } from "@/admin/components/ui";

const empty = {
  single_photo_credits: 10,
  five_photo_credits: 45,
  ten_photo_credits: 80,
  fifteen_photo_credits: 110,
  twenty_photo_credits: 140,
  status: "draft",
  notes: "",
};

export const EventPricingPanel = ({ eventId }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    adminApi.get(`/admin/events/${eventId}/pricing`)
      .then((r) => { if (active && r.data?.pricing) setForm({ ...empty, ...r.data.pricing }); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [eventId]);

  const upd = (k) => (e) => setForm({ ...form, [k]: k === "status" || k === "notes" ? e.target.value : Number(e.target.value) });

  const savings = (pkg, qty) => {
    const single = Number(form.single_photo_credits) || 0;
    const p = Number(form[pkg]) || 0;
    return single * qty - p;
  };

  const warnings = [];
  if (savings("five_photo_credits", 5) <= 0) warnings.push("5-photo package gives no discount vs single price.");
  if (savings("ten_photo_credits", 10) <= 0) warnings.push("10-photo package gives no discount vs single price.");
  if (savings("fifteen_photo_credits", 15) <= 0) warnings.push("15-photo package gives no discount vs single price.");
  if (savings("twenty_photo_credits", 20) <= 0) warnings.push("20-photo package gives no discount vs single price.");

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/events/${eventId}/pricing`, form);
      toast.success("Pricing saved.");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  if (loading) return null;

  return (
    <Panel title="Event Pricing (Credits)" testId="event-pricing-panel"
      action={<Coins className="w-4 h-4 text-[#ff2e93]" />}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <AdminInput data-testid="pricing-single" label="1 Photo" type="number" min={0} value={form.single_photo_credits} onChange={upd("single_photo_credits")} />
        <AdminInput data-testid="pricing-five" label="5 Photos" type="number" min={0} value={form.five_photo_credits} onChange={upd("five_photo_credits")} />
        <AdminInput data-testid="pricing-ten" label="10 Photos" type="number" min={0} value={form.ten_photo_credits} onChange={upd("ten_photo_credits")} />
        <AdminInput data-testid="pricing-fifteen" label="15 Photos" type="number" min={0} value={form.fifteen_photo_credits} onChange={upd("fifteen_photo_credits")} />
        <AdminInput data-testid="pricing-twenty" label="20 Photos" type="number" min={0} value={form.twenty_photo_credits} onChange={upd("twenty_photo_credits")} />
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] tracking-luxe uppercase text-white/55">
        <div className="glass rounded-md p-2.5">5 single: <span className="text-white">{(form.single_photo_credits||0)*5}</span> · pkg: <span className="text-[#7fe6ff]">{form.five_photo_credits}</span> · save <span className="text-[#ff9fcf]">{savings("five_photo_credits",5)}</span></div>
        <div className="glass rounded-md p-2.5">10 single: <span className="text-white">{(form.single_photo_credits||0)*10}</span> · pkg: <span className="text-[#7fe6ff]">{form.ten_photo_credits}</span> · save <span className="text-[#ff9fcf]">{savings("ten_photo_credits",10)}</span></div>
        <div className="glass rounded-md p-2.5">15 single: <span className="text-white">{(form.single_photo_credits||0)*15}</span> · pkg: <span className="text-[#7fe6ff]">{form.fifteen_photo_credits}</span> · save <span className="text-[#ff9fcf]">{savings("fifteen_photo_credits",15)}</span></div>
        <div className="glass rounded-md p-2.5">20 single: <span className="text-white">{(form.single_photo_credits||0)*20}</span> · pkg: <span className="text-[#7fe6ff]">{form.twenty_photo_credits}</span> · save <span className="text-[#ff9fcf]">{savings("twenty_photo_credits",20)}</span></div>
      </div>

      {warnings.length > 0 && (
        <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md border border-[#ff9fcf]/30 bg-[#ff2e93]/5 text-[11px] text-[#ff9fcf]">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <ul className="space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <AdminSelect data-testid="pricing-status" label="Status" value={form.status} onChange={upd("status")}>
          <option value="draft" className="bg-black">Draft</option>
          <option value="active" className="bg-black">Active</option>
          <option value="disabled" className="bg-black">Disabled</option>
        </AdminSelect>
        <AdminTextarea data-testid="pricing-notes" label="Admin Pricing Notes" rows={2} value={form.notes || ""} onChange={upd("notes")} className="md:col-span-2" />
      </div>

      <div className="flex justify-end mt-5">
        <button data-testid="pricing-save" onClick={save} disabled={busy} className="btn-primary !py-2.5 !px-5 !text-[11px] disabled:opacity-60">
          <Save className="w-3.5 h-3.5" /> Save Pricing
        </button>
      </div>
    </Panel>
  );
};

export default EventPricingPanel;
